import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Input } from 'src/components/ui/input';
import { Button } from 'src/components/ui/button';
import { Loader2, Search as SearchIcon, User, X } from 'lucide-react';
import { format } from 'date-fns';
import DataTable from 'src/components/ui/data-table';
import TablePagination from 'src/components/common/TablePagination';
import {
  useSearchResults,
  useCandidateSuggest,
} from 'src/services/api/queries/searchQueries';

const getInitials = (firstName, middleName, lastName) => {
  const parts = [firstName, lastName].filter(Boolean);
  return (
    parts
      .map((p) => p[0].toUpperCase())
      .join('')
      .slice(0, 2) || '?'
  );
};

const formatName = (firstName, middleName, lastName) =>
  [firstName, middleName, lastName].filter(Boolean).join(' ');

const StateScreen = ({ icon: Icon, title, desc }) => (
  <div className="flex flex-col items-center justify-center text-center py-[54px] px-6 bg-card border border-border rounded-xl">
    <div className="w-[52px] h-[52px] rounded-full bg-brand-bg-subtle text-primary flex items-center justify-center mb-[14px]">
      <Icon className="w-[23px] h-[23px]" strokeWidth={1.8} />
    </div>
    <h2 className="text-[16px] font-[650] text-foreground-strong mt-0 mb-[6px]">
      {title}
    </h2>
    <p className="text-[13px] text-muted-foreground m-0 max-w-[380px] leading-[1.55]">
      {desc}
    </p>
  </div>
);

function Search() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Live filter state — bound to the input fields
  const [filters, setFilters] = useState({
    name: searchParams.get('name') || '',
    email: searchParams.get('email') || '',
    phone: searchParams.get('phone') || '',
  });

  // Committed filter state — only updated when the user explicitly searches
  // or selects a suggestion. This is what drives the API call.
  const [searchFilters, setSearchFilters] = useState(null);

  const [debouncedName, setDebouncedName] = useState(filters.name);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameFieldRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(filters.name), 300);
    return () => clearTimeout(t);
  }, [filters.name]);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (nameFieldRef.current && !nameFieldRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Restore search state from URL on mount (supports page refresh / shared links)
  useEffect(() => {
    const urlName = searchParams.get('name') || '';
    const urlEmail = searchParams.get('email') || '';
    const urlPhone = searchParams.get('phone') || '';
    if (urlName || urlEmail || urlPhone) {
      const restored = { name: urlName, email: urlEmail, phone: urlPhone };
      setFilters(restored);
      setSearchFilters(restored);
    }
    if (!searchParams.has('pageSize')) {
      const next = new URLSearchParams(searchParams);
      next.set('pageSize', '10');
      setSearchParams(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSearched = searchFilters !== null;
  const hasFilters = Object.values(filters).some((v) => v.trim() !== '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);

  // Driven by searchFilters (committed), not live filters — typing never triggers this
  const {
    data: response,
    isLoading,
    error,
  } = useSearchResults({
    email: searchFilters?.email,
    contact: searchFilters?.phone,
    name: searchFilters?.name,
    pageNo: page,
    pageSize,
  });

  const {
    data: suggestResponse,
    isFetching: isSuggesting,
    isError: isSuggestError,
  } = useCandidateSuggest({ name: debouncedName });

  const suggestions = suggestResponse?.data?.items ?? [];

  const searchResults = response?.data
    ? {
        items: response.data.items,
        total: response.data.pagination.total,
        page: response.data.pagination.page,
        pageSize: response.data.pagination.page_size,
        totalPages: response.data.pagination.total_pages,
      }
    : { items: [], total: 0, page: 1, pageSize: 10, totalPages: 1 };

  // Commits a set of filters to the API and updates the URL
  const commitSearch = useCallback(
    (activeFilters) => {
      setSearchFilters({ ...activeFilters });
      const newParams = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value.trim()) newParams.set(key, value.trim());
      });
      newParams.set('page', '1');
      newParams.set('pageSize', pageSize.toString());
      setSearchParams(newParams);
    },
    [pageSize, setSearchParams]
  );

  const handleSearch = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        setShowSuggestions(false);
      }
      commitSearch(filters);
    },
    [filters, commitSearch]
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name === 'name') {
      setShowSuggestions(value.trim().length >= 2);
    }
  };

  const handleClearField = (fieldName) => {
    setFilters((prev) => ({ ...prev, [fieldName]: '' }));
    if (fieldName === 'name') setShowSuggestions(false);
  };

  // Selecting a suggestion immediately commits the search
  const handleSelectSuggestion = (suggestion) => {
    const newFilters = { ...filters, name: suggestion };
    setFilters(newFilters);
    setShowSuggestions(false);
    commitSearch(newFilters);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Escape') setShowSuggestions(false);
  };

  const handleReset = () => {
    setFilters({ name: '', email: '', phone: '' });
    setSearchFilters(null);
    setShowSuggestions(false);
    navigate('/candidates/search');
  };

  const navigateToProfile = (candidateId) =>
    navigate(`/candidates/${candidateId}`, {
      state: { from: 'search', searchQuery: searchParams.toString() },
    });

  const columns = [
    {
      key: 'name',
      header: 'Candidate',
      minWidth: '200px',
      render: (_v, row) => (
        <div className="flex items-center gap-[11px]">
          <span className="w-8 h-8 min-w-[32px] rounded-full bg-brand-bg-subtle text-primary flex items-center justify-center text-[11px] font-bold shrink-0">
            {getInitials(row.first_name, row.middle_name, row.last_name)}
          </span>
          <span className="text-[13.5px] font-semibold text-foreground-strong">
            {formatName(row.first_name, row.middle_name, row.last_name)}
          </span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (v) => (
        <a
          href={`mailto:${v}`}
          className="text-[12.5px] text-muted-foreground hover:text-primary transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {v}
        </a>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (v) =>
        v ? (
          <a
            href={`tel:${v}`}
            className="text-[12.5px] text-muted-foreground hover:text-primary transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {v}
          </a>
        ) : (
          <span className="text-foreground-muted">—</span>
        ),
    },
    {
      key: 'created_at',
      header: 'Added',
      render: (_v, row) => (
        <span className="text-[12.5px] text-muted-foreground">
          {format(new Date(row.created_at), 'dd MMM yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '80px',
      render: (_v, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigateToProfile(row.id);
          }}
          className="inline-flex items-center gap-[5px] text-primary font-semibold text-[12.5px] hover:underline"
        >
          <User className="w-[14px] h-[14px]" />
          View
        </button>
      ),
    },
  ];

  const showResults =
    hasSearched && !isLoading && !error && searchResults.items.length > 0;

  return (
    <div>
      <PageHeader title="Search Candidates" />
      <PageContainer>
        <header className="mb-[22px]">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong mb-1">
            Search Candidates
          </h1>
          <p className="text-[13.5px] text-muted-foreground leading-[1.55]">
            Search your candidate pool by name, email, or phone. Combine fields
            to narrow results.
          </p>
        </header>

        {/* Search form card */}
        <div className="bg-card border border-border rounded-xl p-5 mb-[22px]">
          <form onSubmit={handleSearch} noValidate>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-[16px]">
              {/* Name field with suggest dropdown */}
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                className="flex flex-col gap-[6px] relative"
                ref={nameFieldRef}
              >
                <span className="text-[12.5px] font-semibold text-foreground-strong">
                  Name
                </span>
                <div className="relative">
                  <Input
                    name="name"
                    value={filters.name}
                    onChange={handleFilterChange}
                    onFocus={() => {
                      if (
                        filters.name.trim().length >= 2 &&
                        suggestions.length > 0
                      )
                        setShowSuggestions(true);
                    }}
                    onKeyDown={handleNameKeyDown}
                    placeholder="e.g. Varun Sharma"
                    className="h-[40px] text-[13px] pr-8"
                    autoComplete="off"
                  />
                  {filters.name && (
                    <button
                      type="button"
                      onClick={() => handleClearField('name')}
                      aria-label="Clear name"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground-strong transition-colors"
                    >
                      <X className="w-[13px] h-[13px]" />
                    </button>
                  )}
                </div>
                {showSuggestions && (
                  <div className="absolute z-20 top-full left-0 right-0 mt-[3px] bg-card border border-border rounded-xl shadow-pop overflow-hidden">
                    {isSuggesting && (
                      <div className="flex items-center justify-center py-3 gap-2 text-[12.5px] text-muted-foreground">
                        <Loader2 className="w-[13px] h-[13px] animate-spin" />
                        Searching...
                      </div>
                    )}
                    {!isSuggesting && suggestions.length > 0 && (
                      <ul
                        role="listbox"
                        className="py-[6px] max-h-[220px] overflow-y-auto"
                      >
                        {suggestions.map((name) => (
                          <li
                            key={name}
                            role="option"
                            aria-selected={filters.name === name}
                          >
                            <button
                              type="button"
                              className="w-full text-left px-[14px] py-[8px] text-[13px] text-foreground-strong hover:bg-brand-bg-subtle hover:text-primary transition-colors"
                              onClick={() => handleSelectSuggestion(name)}
                            >
                              {name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {!isSuggesting && suggestions.length === 0 && (
                      <p className="px-[14px] py-[10px] text-[12.5px] text-muted-foreground">
                        {isSuggestError
                          ? 'Could not load suggestions.'
                          : 'No candidates found.'}
                      </p>
                    )}
                  </div>
                )}
              </label>

              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="flex flex-col gap-[6px]">
                <span className="text-[12.5px] font-semibold text-foreground-strong">
                  Email
                </span>
                <div className="relative">
                  <Input
                    name="email"
                    type="email"
                    value={filters.email}
                    onChange={handleFilterChange}
                    placeholder="e.g. name@email.com"
                    className="h-[40px] text-[13px] pr-8"
                  />
                  {filters.email && (
                    <button
                      type="button"
                      onClick={() => handleClearField('email')}
                      aria-label="Clear email"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground-strong transition-colors"
                    >
                      <X className="w-[13px] h-[13px]" />
                    </button>
                  )}
                </div>
              </label>

              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="flex flex-col gap-[6px]">
                <span className="text-[12.5px] font-semibold text-foreground-strong">
                  Phone
                </span>
                <div className="relative">
                  <Input
                    name="phone"
                    value={filters.phone}
                    onChange={handleFilterChange}
                    placeholder="e.g. 98765"
                    className="h-[40px] text-[13px] pr-8"
                  />
                  {filters.phone && (
                    <button
                      type="button"
                      onClick={() => handleClearField('phone')}
                      aria-label="Clear phone"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground-strong transition-colors"
                    >
                      <X className="w-[13px] h-[13px]" />
                    </button>
                  )}
                </div>
              </label>
            </div>

            <div className="flex items-center gap-[10px] mt-[16px]">
              <Button
                type="submit"
                disabled={isLoading || !hasFilters}
                className="h-auto py-[9px] px-[20px] bg-primary hover:bg-brand-deep"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-[14px] w-[14px] animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <SearchIcon className="h-[14px] w-[14px]" />
                    Search
                  </>
                )}
              </Button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading || (!hasFilters && !hasSearched)}
                className="inline-flex items-center gap-[6px] px-[16px] py-[9px] text-[13px] font-medium text-muted-foreground bg-transparent border-0 rounded-lg hover:bg-brand-subtle hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="h-[14px] w-[14px]" />
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Prompt state — shown before any search */}
        {!hasSearched && (
          <StateScreen
            icon={SearchIcon}
            title="Search your candidate pool"
            desc="Enter a name, email, or phone above to find candidates."
          />
        )}

        {/* Error state */}
        {hasSearched && error && (
          <div className="text-center py-6 text-ds-danger text-[13px]">
            {error.message}
          </div>
        )}

        {/* Results area */}
        {hasSearched && !error && (
          <>
            {showResults && (
              <div className="flex items-center justify-between mb-[12px]">
                <h2 className="text-[15px] font-[650] text-foreground-strong m-0">
                  Results{' '}
                  <span className="text-foreground-muted font-medium">
                    ({searchResults.total})
                  </span>
                </h2>
              </div>
            )}

            {isLoading && <DataTable data={[]} columns={columns} loading />}

            {!isLoading && searchResults.items.length === 0 && (
              <StateScreen
                icon={SearchIcon}
                title="No candidates found"
                desc="No candidates match your search. Try a different name, email, or phone."
              />
            )}

            {!isLoading && searchResults.items.length > 0 && (
              <>
                <DataTable
                  data={searchResults.items}
                  columns={columns}
                  hover
                  stickyHeader
                  onRowClick={(row) => navigateToProfile(row.id)}
                />
                <TablePagination
                  currentPage={page}
                  totalPages={searchResults.totalPages}
                  pageSize={pageSize}
                  totalItems={searchResults.total}
                  onPageChange={(newPage) => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('page', newPage.toString());
                    setSearchParams(newParams);
                  }}
                  onPageSizeChange={(newSize) => {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('pageSize', newSize.toString());
                    newParams.set('page', '1');
                    setSearchParams(newParams);
                  }}
                  itemLabel="candidates"
                  className="mt-4"
                />
              </>
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
}

export default Search;
